// Catalogue helpers: may this person ask for this command here, and is the payload what the command's schema accepts?
// The engine and the database decide in the end (they refuse whatever is wrong); these checks only let the UI explain the
// refusal before sending, and never widen what the engine allows.
import { ROLE_RANK } from "../data/source";
import type { CommandSpec, JsonSchema, Member, ToFill } from "../data/types";

/** Fields the browser must never supply: the actor and the agent are stamped by the database and the engine (rules 4, 5). */
export const FORBIDDEN_FIELDS = new Set(["actor", "agent", "instruction", "person_name", "by", "channel"]);
/** Fields implied by the inbox row itself: an engagement command's `engagement` is the row's engagement_id (UI_DATA §4). */
export const IMPLIED_FIELDS = new Set(["engagement"]);

export interface Allowed { ok: boolean; reason: string | null }

export function mayRequest(spec: CommandSpec | undefined, me: Member, scope: "engagement" | "firm" = spec?.scope ?? "engagement"): Allowed {
  if (!spec) return { ok: false, reason: "This command is not in the engine's command catalogue." };
  if (me.role === "client") return { ok: false, reason: "Only members of the firm ask the engine to act." };
  if (spec.route === "local") return { ok: false, reason: `Runs only on the engine's machine: yvc ${spec.name.replace(".", " ")}` };
  if (spec.scope !== scope) return { ok: false, reason: `This is a ${spec.scope}-level command.` };
  if (ROLE_RANK[me.role] < ROLE_RANK[spec.minRole]) return { ok: false, reason: `Needs the ${spec.minRole} role; you are ${me.role}.` };
  return { ok: true, reason: null };
}

/** Resolve a local `$ref` ("#/$defs/Name") against the command's root schema. */
export function deref(s: JsonSchema, root?: JsonSchema): JsonSchema {
  if (!s.$ref || !root?.$defs) return s;
  const name = s.$ref.split("/").pop() ?? "";
  return { ...(root.$defs[name] ?? {}), title: s.title ?? root.$defs[name]?.title };
}

/** The non-null branch of a pydantic Optional (anyOf [{type: X}, {type: null}]). */
export function effective(s: JsonSchema): { schema: JsonSchema; nullable: boolean } {
  if (s.anyOf?.length) {
    const nonNull = s.anyOf.filter((x) => x.type !== "null");
    const nullable = nonNull.length < s.anyOf.length;
    return { schema: { ...s, ...(nonNull[0] ?? {}), anyOf: undefined, title: s.title ?? nonNull[0]?.title, description: s.description ?? nonNull[0]?.description }, nullable };
  }
  return { schema: s, nullable: false };
}

/** Validate a payload against a command's input schema (the subset pydantic emits). Returns field → message. */
export function validatePayload(spec: CommandSpec, payload: Record<string, unknown>): Record<string, string> {
  const errors: Record<string, string> = {};
  const props = spec.inputSchema.properties ?? {};
  const required = new Set(spec.inputSchema.required ?? []);
  for (const k of Object.keys(payload)) {
    if (FORBIDDEN_FIELDS.has(k)) errors[k] = "The engine stamps this; the browser never supplies it.";
    else if (IMPLIED_FIELDS.has(k)) errors[k] = "Implied by the engagement the request is for; not sent in the payload.";
    else if (!props[k]) errors[k] = "Not a field of this command.";              // the database refuses unknown fields
  }
  for (const [k, raw] of Object.entries(props)) {
    if (IMPLIED_FIELDS.has(k) || FORBIDDEN_FIELDS.has(k)) continue;
    const { schema: s, nullable } = effective(raw);
    const hasDefault = raw.default !== undefined || s.default !== undefined;
    const v = payload[k];
    const empty = v === undefined || v === null || v === "" || (Array.isArray(v) && v.length === 0);
    if (empty) { if (required.has(k) && !nullable && !hasDefault) errors[k] = "Required."; continue; }
    if (s.enum && !s.enum.includes(v as string | number)) errors[k] = `One of: ${s.enum.join(", ")}.`;
    else if (s.type === "string" && typeof v !== "string") errors[k] = "Text expected.";
    else if (s.type === "string" && s.minLength && (v as string).trim().length < s.minLength) errors[k] = `At least ${s.minLength} characters.`;
    else if (s.type === "string" && s.maxLength && (v as string).length > s.maxLength) errors[k] = `At most ${s.maxLength} characters.`;
    else if ((s.type === "integer" || s.type === "number") && (typeof v !== "number" || !Number.isFinite(v))) errors[k] = "A number expected.";
    else if (s.type === "integer" && !Number.isInteger(v)) errors[k] = "A whole number expected.";
    else if (s.type === "boolean" && typeof v !== "boolean") errors[k] = "Yes or no expected.";
    else if (s.type === "array" && !Array.isArray(v)) errors[k] = "A list expected.";
    else if (s.type === "array" && s.minItems && (v as unknown[]).length < s.minItems) errors[k] = `At least ${s.minItems}.`;
    else if (s.minimum !== undefined && typeof v === "number" && v < s.minimum) errors[k] = `At least ${s.minimum}.`;
    else if (s.maximum !== undefined && typeof v === "number" && v > s.maximum) errors[k] = `At most ${s.maximum}.`;
  }
  return errors;
}

/** Only the fields the schema knows, without empties (a pristine optional field is omitted, not sent as ""). */
export function cleanPayload(spec: CommandSpec, payload: Record<string, unknown>): Record<string, unknown> {
  const props = spec.inputSchema.properties ?? {};
  const out: Record<string, unknown> = {};
  for (const [k, v] of Object.entries(payload)) {
    if (FORBIDDEN_FIELDS.has(k) || IMPLIED_FIELDS.has(k)) continue;
    if (!props[k]) continue;
    if (v === undefined || v === "" || (Array.isArray(v) && v.length === 0)) continue;
    out[k] = v;
  }
  return out;
}

/** Ids the read models carry as text ("14") sent as numbers where the command's schema wants an integer or a number. */
export function coerceNumbers(spec: CommandSpec, payload: Record<string, unknown>): Record<string, unknown> {
  const props = spec.inputSchema.properties ?? {};
  return Object.fromEntries(Object.entries(payload).map(([k, v]) => {
    const t = props[k] ? effective(props[k]).schema.type : undefined;
    return [k, (t === "integer" || t === "number") && typeof v === "string" && /^-?\d+(\.\d+)?$/.test(v) ? Number(v) : v];
  }));
}

export const byName = (cat: CommandSpec[]) => new Map(cat.map((c) => [c.name, c]));

/** Split a command line the way a shell would for simple quoting: yvc review respond --id 1 --response "a b". */
export function tokenize(line: string): string[] {
  const out: string[] = [];
  const re = /"((?:[^"\\]|\\.)*)"|'([^']*)'|(\S+)/g;
  let m: RegExpExecArray | null;
  while ((m = re.exec(line))) out.push(m[1] !== undefined ? m[1].replace(/\\(.)/g, "$1") : m[2] !== undefined ? m[2] : m[3]);
  return out;
}

const PLACEHOLDER = /^(\.\.\.|<.*>|…)$/;
/**
 * The inputs an action names, read from its `run` line (work.next publishes the exact command line, not a payload):
 * `--flag value` pairs typed by the command's schema. Placeholders ("...", "<what you read>") are left for the person to
 * fill, and `engagement` is implied by the row. Nothing is invented: a flag the schema does not know is dropped.
 */
export function payloadFromRun(run: string | null | undefined, spec: CommandSpec | undefined): Record<string, unknown> {
  if (!run || !spec) return {};
  const props = spec.inputSchema.properties ?? {};
  const t = tokenize(run);
  const out: Record<string, unknown> = {};
  for (let i = 0; i < t.length; i++) {
    if (!t[i].startsWith("--")) continue;
    const name = t[i].slice(2).replace(/-/g, "_");
    let next = t[i + 1];
    const hasValue = next !== undefined && !next.startsWith("--");
    if (hasValue) i++;
    if (hasValue && next.startsWith("<") && !next.endsWith(">")) {        // an unquoted <placeholder with spaces>
      while (i + 1 < t.length && !t[i].endsWith(">")) i++;
      next = "<>";
    }
    if (IMPLIED_FIELDS.has(name) || FORBIDDEN_FIELDS.has(name) || !props[name]) continue;
    const { schema } = effective(deref(props[name], spec.inputSchema));
    if (schema.type === "boolean") { out[name] = hasValue ? next === "true" || next === "1" : true; continue; }
    if (!hasValue || PLACEHOLDER.test(next)) continue;
    const typed = (s: JsonSchema | undefined, v: string) => (s?.type === "integer" || s?.type === "number" ? (Number.isFinite(Number(v)) ? Number(v) : v) : v);
    if (schema.type === "array") out[name] = [...((out[name] as unknown[]) ?? []), typed(schema.items, next)];
    else out[name] = typed(schema, next);
  }
  return out;
}

/** The payload for an action: what it published, else what its run line names. */
export const actionPayload = (a: { payload: Record<string, unknown>; run: string | null }, spec: CommandSpec | undefined) =>
  Object.keys(a.payload).length ? a.payload : payloadFromRun(a.run, spec);

/** Who an action is for, as the engine wrote it in `who`: this person, anyone on the team, or someone else. */
export function addressedTo(who: string | null | undefined, personName: string, role: string): "me" | "anyone" | "other" {
  const w = (who ?? "").trim();
  if (!w) return "anyone";
  const but = w.match(/^anyone but (.+)$/i);
  if (but) return but[1].split(",").map((x) => x.trim()).includes(personName) ? "other" : "anyone";
  const names = w.split(",").map((x) => x.trim());                   // "Maya Chen, Tinbite Mamo": any of these people
  return names.includes(personName) || names.includes(role) ? "me" : "other";
}

/** The command's schema with what the engine's action says a person must still fill (`payload.to_fill`): required ones become
 * required, and each field carries the engine's hint. The engine only narrows what the schema accepts; nothing is prefilled. */
export function withToFill(spec: CommandSpec, toFill: ToFill[] | undefined): CommandSpec {
  if (!toFill?.length) return spec;
  const props = { ...(spec.inputSchema.properties ?? {}) };
  for (const f of toFill) {
    const p = props[f.field];
    if (!p) continue;
    const hint = f.hint && f.hint.replace(/_/g, " ").toLowerCase() !== f.field.replace(/_/g, " ").toLowerCase() ? f.hint : null;
    const description = [p.description, hint ? `The engine asks: ${hint}` : null].filter(Boolean).join(" — ") || undefined;
    props[f.field] = f.required ? { ...p, anyOf: undefined, ...effective(p).schema, default: undefined, description } : { ...p, description };
  }
  const required = [...new Set([...(spec.inputSchema.required ?? []), ...toFill.filter((f) => f.required && props[f.field]).map((f) => f.field)])];
  return { ...spec, inputSchema: { ...spec.inputSchema, properties: props, required } };
}
